import React from 'react';
import { about } from '@/data/content';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';

const AboutPhilosophy: React.FC = () => {
    const paragraphs = about.body.split(/\n\n+/).filter(Boolean);
    const [img1, img2] = about.images ?? [];

    return (
        <section className="py-20 md:py-32 bg-gray-50 dark:bg-gray-800/50 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="flex-1 relative w-full"
                    >
                        <div className="relative z-10 grid grid-cols-2 gap-4">
                            <div className="space-y-4 translate-y-8">
                                {img1 && (
                                    <div className="rounded-3xl overflow-hidden shadow-xl aspect-square">
                                        <img
                                            src={img1.src}
                                            alt={img1.alt}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                )}
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    className="bg-app-primary p-6 rounded-3xl text-white shadow-xl"
                                >
                                    <h3 className="text-4xl font-black mb-2 text-white">{about.stat.value}</h3>
                                    <p className="font-medium text-white/80 leading-snug">{about.stat.label}</p>
                                </motion.div>
                            </div>
                            <div className="space-y-4">
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    className="bg-white dark:bg-gray-900 p-6 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800"
                                >
                                    <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xl mb-4">
                                        🌿
                                    </div>
                                    <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                                        {about.zero_waste_card.title}
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {about.zero_waste_card.description}
                                    </p>
                                </motion.div>
                                {img2 && (
                                    <div className="rounded-3xl overflow-hidden shadow-xl aspect-[4/5]">
                                        <img
                                            src={img2.src}
                                            alt={img2.alt}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-orange-100 dark:bg-gray-800 rounded-full blur-3xl -z-10 animate-pulse-slow opacity-60"></div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="flex-1"
                    >
                        <span className="inline-block px-4 py-1.5 rounded-full bg-app-primary/10 text-app-primary font-bold text-sm tracking-wide uppercase mb-4 shadow-sm">
                            {about.philosophy.title}
                        </span>
                        <h2 className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-6 leading-tight">
                            {about.philosophy_heading}
                        </h2>

                        <div className="space-y-6 text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                            {paragraphs.slice(0, 2).map((para, idx) => (
                                <p key={idx}>{para}</p>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                            {about.philosophy.items.map((item, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: 10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="flex items-center gap-3"
                                >
                                    <div className="w-6 h-6 rounded-full bg-app-primary/10 text-app-primary flex items-center justify-center text-sm font-bold shrink-0">
                                        ✓
                                    </div>
                                    <span className="font-semibold text-gray-900 dark:text-gray-300">{item}</span>
                                </motion.div>
                            ))}
                        </div>

                        <Button
                            to="/about-us"
                            variant="ghost"
                            className="group flex items-center gap-3 p-0"
                            icon={
                                <span className="w-10 h-10 rounded-full bg-app-primary/10 flex items-center justify-center group-hover:bg-app-primary group-hover:text-white transition-colors">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                                        />
                                    </svg>
                                </span>
                            }
                        >
                            {about.story_cta}
                        </Button>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default AboutPhilosophy;
